import React, { useState, useEffect } from 'react';
import {
  Box,
  Button,
  Container,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Switch,
  FormControlLabel,
  Grid,
  Tab,
  Tabs,
  CircularProgress,
  Chip,
  Tooltip,
  Alert
} from '@mui/material';
import { 
  Add as AddIcon, 
  Edit as EditIcon, 
  Delete as DeleteIcon,
  ColorLens as ColorLensIcon,
  Image as ImageIcon,
  CheckCircle as CheckCircleIcon,
  RadioButtonUnchecked as RadioButtonUncheckedIcon
} from '@mui/icons-material';
import { useForm } from 'react-hook-form';
import AdminLayout from './components/AdminLayout';
import tenantService, { Tenant, TenantBranding, TenantBrandingText } from '../../services/tenantService';
import apiClient from '../../services/apiClient';

// We use the Tenant and TenantBranding interfaces directly from tenantService
// They have been enhanced to include all the fields we need

// Form data types
type TenantFormData = Pick<Tenant, 'name' | 'domain' | 'active' | 'siteName' | 'presentsText' | 'contactEmail' | 'contactPhone' | 'timezone' | 'language' | 'tagline' | 'footerText' | 'privacyPolicyUrl' | 'termsUrl' | 'gaTrackingId'>;
type BrandingFormData = Omit<TenantBranding, 'tenantId' | 'brandingId'>;

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

// Tab Panel component
function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`tenant-tabpanel-${index}`}
      aria-labelledby={`tenant-tab-${index}`}
      {...other}
    >
      {value === index && (
        <Box sx={{ p: 3 }}>
          {children}
        </Box>
      )}
    </div>
  );
}

const TenantManagementPage: React.FC = () => {
  const [tenants, setTenants] = useState<Tenant[]>([]);
  const [selectedTenant, setSelectedTenant] = useState<Tenant | null>(null);
  const [tenantBrandingColors, setTenantBrandingColors] = useState<{
    primaryColor: string;
    secondaryColor: string;
  }>({
    primaryColor: '#2B3990',
    secondaryColor: '#4CAF50'
  });
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [openTenantDialog, setOpenTenantDialog] = useState<boolean>(false);
  const [openBrandingDialog, setOpenBrandingDialog] = useState<boolean>(false);
  const [openDeleteDialog, setOpenDeleteDialog] = useState<boolean>(false);
  const [tabValue, setTabValue] = useState<number>(0);
  const [switchingTenant, setSwitchingTenant] = useState<boolean>(false);
  
  const tenantForm = useForm<Tenant>();
  const brandingForm = useForm<TenantBranding>();

  // Component initialization 
  useEffect(() => {
    console.log('[TenantManagementPage] Component mounted');
    
    // Fetch tenants on component mount
    fetchTenants();
    
    // Check and clear switching flag if present (prevents infinite reload loops)
    const isSwitchingFlag = sessionStorage.getItem('tenant_switching');
    if (isSwitchingFlag) {
      console.log('[TenantManagementPage] Clearing tenant_switching flag from sessionStorage');
      sessionStorage.removeItem('tenant_switching');
    }
    
    // Cleanup on unmount
    return () => {
      console.log('[TenantManagementPage] Component unmounting');
    };
  }, []);

  // Fetch tenants from API
  const fetchTenants = async () => {
    setLoading(true);
    try {
      console.log('[TenantManagementPage] Fetching tenants from API');
      const tenantsData = await tenantService.getAllTenants();
      console.log(`[TenantManagementPage] Received ${tenantsData.length} tenants from API`);

      // Log each tenant's isCurrent flag for debugging
      tenantsData.forEach(tenant => {
        console.log(`[TenantManagementPage] Tenant ${tenant.tenantId} (${tenant.name}): isCurrent = ${tenant.isCurrent}`);
      });

      // Just use the data as provided by the server
      setTenants(tenantsData);
      setError(null);
    } catch (err) {
      console.error('[TenantManagementPage] Error fetching tenants:', err);
      setError('Failed to load tenants. Please try again later.');
    } finally {
      setLoading(false);
    }
  };
  
  // Switch to a different tenant
  const handleSwitchTenant = async (tenantId: number) => {
    if (switchingTenant) {
      console.log('[TenantManagementPage] Already switching tenant, ignoring request');
      return;
    }
    
    if (!tenantId || isNaN(tenantId)) {
      console.error(`[TenantManagementPage] Invalid tenant ID: ${tenantId}`);
      setError('Invalid tenant ID specified');
      return;
    }
    
    // Check if this tenant is already the current one
    const currentTenant = tenants.find(t => t.isCurrent);
    if (currentTenant?.tenantId === tenantId) {
      console.log(`[TenantManagementPage] Tenant ${tenantId} is already current, no switch needed`);
      return;
    }
    
    console.log(`[TenantManagementPage] Switching to tenant ${tenantId}`);
    setSwitchingTenant(true);
    
    try {
      // Call the API to switch tenants
      await tenantService.switchToTenant(tenantId);
      
      // The page will reload and the server will return the correct is_current flags
      // No need to manually update the UI
    } catch (err) {
      console.error('[TenantManagementPage] Error switching tenant:', err);
      setError('Failed to switch tenant. Please try again.');
      setSwitchingTenant(false);
    }
    // No finally block because page should reload if successful
  };

  // Fetch branding for a specific tenant
  const fetchTenantBranding = async (tenantId: number) => {
    try {
      const brandingData = await tenantService.getTenantBranding(tenantId);
      return brandingData;
    } catch (err) {
      console.error(`Error fetching branding for tenant ${tenantId}:`, err);
      return null;
    }
  };

  // Handle tab change
  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setTabValue(newValue);
  };

  // Open tenant dialog for adding/editing
  const handleOpenTenantDialog = async (tenant: Tenant | null = null) => {
    setSelectedTenant(tenant);
    
    // Reset branding colors to defaults - using tertiary color (amber) for secondary
    setTenantBrandingColors({
      primaryColor: '#2B3990', // Deep blue
      secondaryColor: '#F39C12'  // Amber/yellow for the "presents" text
    });
    
    if (tenant) {
      tenantForm.reset({
        tenantId: tenant.tenantId,
        name: tenant.name,
        domain: tenant.domain,
        siteName: tenant.siteName || 'MathWiz League',
        presentsText: tenant.presentsText || '',
        active: tenant.active,
        createdAt: tenant.createdAt,
        updatedAt: tenant.updatedAt
      });

      // Fetch branding colors if tenant exists
      try {
        const branding = await fetchTenantBranding(tenant.tenantId);
        if (branding) {
          setTenantBrandingColors({
            primaryColor: branding.primaryColor,
            secondaryColor: branding.secondaryColor
          });
        }
      } catch (err) {
        console.error('Error fetching tenant branding for preview:', err);
      }
    } else {
      tenantForm.reset({
        name: '',
        domain: '',
        siteName: 'MathWiz League',
        presentsText: '',
        active: true
      } as Partial<Tenant>);
    }
    
    setOpenTenantDialog(true);
  };

  // Open branding dialog for adding/editing
  const handleOpenBrandingDialog = async (tenant: Tenant) => {
    setSelectedTenant(tenant);
    const branding = await fetchTenantBranding(tenant.tenantId);

    if (branding) {
      brandingForm.reset({
        brandingId: branding.brandingId,
        tenantId: branding.tenantId,
        primaryColor: branding.primaryColor,
        secondaryColor: branding.secondaryColor,
        tertiaryColor: branding.tertiaryColor,
        accentColor: branding.accentColor,
        fontFamily: branding.fontFamily,
        logoUrl: branding.logoUrl || '',
        faviconUrl: branding.faviconUrl || '',
        customCss: branding.customCss || ''
      });
    } else {
      brandingForm.reset({
        tenantId: tenant.tenantId,
        primaryColor: '#2B3990',
        secondaryColor: '#4CAF50',
        tertiaryColor: '#F39C12',
        accentColor: '#9C27B0',
        fontFamily: "'Poppins', 'Roboto', 'Helvetica', 'Arial', sans-serif",
        logoUrl: '',
        faviconUrl: '',
        customCss: ''
      } as Partial<TenantBranding>);
    }

    setOpenBrandingDialog(true);
  };

  // Open delete confirmation dialog
  const handleOpenDeleteDialog = (tenant: Tenant) => {
    setSelectedTenant(tenant);
    setOpenDeleteDialog(true);
  };

  // Close dialogs
  const handleCloseTenantDialog = () => {
    setOpenTenantDialog(false);
    setSelectedTenant(null);
  };

  const handleCloseBrandingDialog = () => {
    setOpenBrandingDialog(false);
  };

  const handleCloseDeleteDialog = () => {
    setOpenDeleteDialog(false);
    setSelectedTenant(null);
  };

  // Submit tenant form
  const handleTenantSubmit = async (data: TenantFormData) => {
    try {
      if (selectedTenant) {
        // For existing tenants, handle the two cases separately:
        
        // 1. Store current values to restore them if needed
        const originalName = selectedTenant.name;
        const originalActive = selectedTenant.active;
        
        // Check if any of the non-domain fields changed
        const nameChanged = originalName !== data.name;
        const activeChanged = originalActive !== data.active;
        const siteNameChanged = (selectedTenant.siteName || 'MathWiz League') !== data.siteName;
        const presentsTextChanged = (selectedTenant.presentsText || '') !== data.presentsText;
        
        // 2. Save the branding text fields directly to the tenant in database using the tenant service
        //    This approach stores them in the right place in the database
        if (siteNameChanged || presentsTextChanged) {
          try {
            const updateData: TenantBrandingText = {
              site_name: data.siteName || 'MathWiz League',
              presents_text: data.presentsText || ''
            };

            // Use the service to update branding text
            await tenantService.updateTenantBrandingText(selectedTenant.tenantId, updateData);
          } catch (err) {
            console.error('Error updating tenant branding text:', err);
            throw err;
          }
        }

        // 3. If name or active status changed, update them separately
        if (nameChanged || activeChanged) {
          try {
            const nameUpdateData = {
              name: data.name,
              active: data.active
            };

            // We still need to use the API client directly since tenantService doesn't have
            // an updateTenant method yet. In a future refactoring we should add this method.
            await apiClient.put(`/api/tenants/${selectedTenant.tenantId}`, nameUpdateData);
          } catch (err) {
            console.error('Error updating tenant name/active status:', err);
            throw err;
          }
        }
      } else {
        // For new tenants, check domain uniqueness
        const existingTenant = tenants.find(tenant => tenant.domain === data.domain);
        
        if (existingTenant) {
          tenantForm.setError('domain', {
            type: 'manual',
            message: `Domain '${data.domain}' is already in use by another tenant`
          });
          return;
        }
        
        // Create new tenant with all fields - still use apiClient directly
        // In a future refactoring we should add createTenant method to the service
        await apiClient.post('/api/tenants', data);
        // Default branding will be created by database triggers or migrations
      }
      
      // Refresh tenants list
      fetchTenants();
      handleCloseTenantDialog();
    } catch (err: unknown) {
      console.error('Error saving tenant:', err);
      // Check for specific error messages from the API
      const error = err as { response?: { data?: { message?: string } }; message?: string };
      if (error.response?.data?.message?.includes('Domain') || error.message?.includes('Domain')) {
        tenantForm.setError('domain', {
          type: 'manual',
          message: error.response?.data?.message || 'This domain is already in use'
        });
      } else {
        setError('Failed to save tenant. Please try again.');
      }
    }
  };
  
  // The branding creation is now handled by database migrations
  // These functions have been removed as they're no longer needed in the frontend

  // Submit branding form
  const handleBrandingSubmit = async (data: BrandingFormData) => {
    if (!selectedTenant) return;

    try {
      // Always use the update method - the backend will create if needed
      await tenantService.updateTenantBranding(selectedTenant.tenantId, data);
      handleCloseBrandingDialog();
    } catch (err) {
      console.error('Error saving branding:', err);
      setError('Failed to save branding. Please try again.');
    }
  };

  // Delete tenant
  const handleDeleteTenant = async () => {
    if (!selectedTenant) return;

    try {
      await apiClient.delete(`/api/tenants/${selectedTenant.tenantId}`);
      fetchTenants();
      handleCloseDeleteDialog();
    } catch (err) {
      console.error('Error deleting tenant:', err);
      setError('Failed to delete tenant. Please try again.');
    }
  };

  return (
    <AdminLayout>
      <Container maxWidth="lg">
        <Box sx={{ mt: 4, mb: 4 }}>
          <Typography variant="h4" component="h1" gutterBottom>
            Tenant Management
          </Typography>
          
          {/* Add Role Switcher component for super admins */}
          {/* <RoleSwitcher 
            tenants={tenants} 
            onRoleSwitch={() => {
              // Refresh the tenant list when role is switched
              fetchTenants();
            }} 
          /> */}
          
          <Box sx={{ width: '100%', mb: 4 }}>
            <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
              <Tabs value={tabValue} onChange={handleTabChange}>
                <Tab label="Tenants" />
                <Tab label="Branding" />
              </Tabs>
            </Box>
            
            <TabPanel value={tabValue} index={0}>
              <Box sx={{ mb: 3 }}>
                <Alert severity="info" sx={{ mb: 2 }}>
                  <Typography variant="subtitle2">Multi-Tenant Controls</Typography>
                  <Typography variant="body2">
                    Only one tenant can be active at a time for super admins to view its content. 
                    Use the radio buttons in the "Current" column to switch between tenants.
                    The tenant with the green checkmark is currently active.
                  </Typography>
                </Alert>
              </Box>
              
              <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 2 }}>
                <Button
                  variant="contained"
                  startIcon={<AddIcon />}
                  onClick={() => handleOpenTenantDialog()}
                >
                  Add Tenant
                </Button>
              </Box>
              
              {loading ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
                  <CircularProgress />
                </Box>
              ) : error ? (
                <Typography color="error" align="center">
                  {error}
                </Typography>
              ) : (
                <TableContainer component={Paper}>
                  <Table>
                    <TableHead>
                      <TableRow>
                        <TableCell>ID</TableCell>
                        <TableCell>Name</TableCell>
                        <TableCell>Domain</TableCell>
                        <TableCell>Site Name</TableCell>
                        <TableCell>Status</TableCell>
                        <TableCell>Created</TableCell>
                        <TableCell>Current</TableCell>
                        <TableCell>Actions</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {tenants.map((tenant) => (
                        <TableRow key={tenant.tenantId}>
                          <TableCell>{tenant.tenantId}</TableCell>
                          <TableCell>{tenant.name}</TableCell>
                          <TableCell>{tenant.domain}</TableCell>
                          <TableCell>{tenant.siteName || 'MathWiz League'}</TableCell>
                          <TableCell>
                            <Chip
                              label={tenant.active ? 'Active' : 'Inactive'}
                              color={tenant.active ? 'success' : 'default'}
                              size="small"
                            />
                          </TableCell>
                          <TableCell>
                            {new Date(tenant.createdAt).toLocaleDateString()}
                          </TableCell>
                          <TableCell align="center">
                            {tenant.isCurrent ? (
                              <Tooltip title="Currently active tenant">
                                <CheckCircleIcon color="success" />
                              </Tooltip>
                            ) : (
                              <Tooltip title="Switch to this tenant">
                                <IconButton
                                  size="small"
                                  onClick={() => handleSwitchTenant(tenant.tenantId)}
                                  disabled={!tenant.active || switchingTenant}
                                >
                                  <RadioButtonUncheckedIcon color={tenant.active ? "primary" : "disabled"} />
                                </IconButton>
                              </Tooltip>
                            )}
                          </TableCell>
                          <TableCell>
                            <IconButton
                              size="small"
                              color="primary"
                              onClick={() => handleOpenTenantDialog(tenant)}
                            >
                              <EditIcon />
                            </IconButton>
                            <IconButton
                              size="small"
                              color="error"
                              onClick={() => handleOpenDeleteDialog(tenant)}
                              disabled={tenant.isCurrent} // Can't delete the current tenant
                            >
                              <DeleteIcon />
                            </IconButton>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              )}
            </TabPanel>
            
            <TabPanel value={tabValue} index={1}>
              <Typography variant="body2" color="textSecondary" paragraph>
                Configure custom branding for each tenant including colors, fonts, and logos.
              </Typography>
              
              {loading ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
                  <CircularProgress />
                </Box>
              ) : error ? (
                <Typography color="error" align="center">
                  {error}
                </Typography>
              ) : (
                <TableContainer component={Paper}>
                  <Table>
                    <TableHead>
                      <TableRow>
                        <TableCell>Tenant</TableCell>
                        <TableCell>Domain</TableCell>
                        <TableCell>Site Name</TableCell>
                        <TableCell>Status</TableCell>
                        <TableCell>Current</TableCell>
                        <TableCell>Branding</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {tenants.map((tenant) => (
                        <TableRow key={tenant.tenantId}>
                          <TableCell>{tenant.name}</TableCell>
                          <TableCell>{tenant.domain}</TableCell>
                          <TableCell>{tenant.siteName || 'MathWiz League'}</TableCell>
                          <TableCell>
                            <Chip
                              label={tenant.active ? 'Active' : 'Inactive'}
                              color={tenant.active ? 'success' : 'default'}
                              size="small"
                            />
                          </TableCell>
                          <TableCell align="center">
                            {tenant.isCurrent ? (
                              <Tooltip title="Currently active tenant">
                                <CheckCircleIcon color="success" />
                              </Tooltip>
                            ) : (
                              <Tooltip title="Switch to this tenant">
                                <IconButton
                                  size="small"
                                  onClick={() => handleSwitchTenant(tenant.tenantId)}
                                  disabled={!tenant.active || switchingTenant}
                                >
                                  <RadioButtonUncheckedIcon color={tenant.active ? "primary" : "disabled"} />
                                </IconButton>
                              </Tooltip>
                            )}
                          </TableCell>
                          <TableCell>
                            <Button
                              variant="outlined"
                              startIcon={<ColorLensIcon />}
                              size="small"
                              onClick={() => handleOpenBrandingDialog(tenant)}
                            >
                              Configure Branding
                            </Button>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              )}
            </TabPanel>
          </Box>
        </Box>
      </Container>
      
      {/* Tenant Dialog */}
      <Dialog open={openTenantDialog} onClose={handleCloseTenantDialog} maxWidth="md" fullWidth>
        <form onSubmit={tenantForm.handleSubmit(handleTenantSubmit)}>
          <DialogTitle>
            {selectedTenant ? 'Edit Tenant' : 'Add Tenant'}
          </DialogTitle>
          <DialogContent>
            <Box sx={{ mt: 2 }}>
              <Grid container spacing={2}>
                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    margin="normal"
                    label="Tenant Name"
                    variant="outlined"
                    {...tenantForm.register('name', { required: 'Name is required' })}
                    error={!!tenantForm.formState.errors.name}
                    helperText={tenantForm.formState.errors.name?.message}
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    margin="normal"
                    label="Domain"
                    variant="outlined"
                    placeholder="example.com"
                    {...tenantForm.register('domain', { required: 'Domain is required' })}
                    error={!!tenantForm.formState.errors.domain}
                    helperText={tenantForm.formState.errors.domain?.message || 'Enter the full domain without http:// or https://'}
                    disabled={!!selectedTenant} // Disable domain field when editing existing tenant
                  />
                </Grid>
              </Grid>

              <Typography variant="h6" sx={{ mt: 3, mb: 1 }}>
                Site Branding
              </Typography>
              
              <Grid container spacing={2}>
                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    margin="normal"
                    label="Site Name"
                    variant="outlined"
                    placeholder="MathWiz League"
                    {...tenantForm.register('siteName', { required: 'Site name is required' })}
                    error={!!tenantForm.formState.errors.siteName}
                    helperText={tenantForm.formState.errors.siteName?.message || "This will replace 'MathWiz League' throughout the site"}
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    margin="normal"
                    label="Presents Text"
                    variant="outlined"
                    placeholder="ACME Corporation presents"
                    {...tenantForm.register('presentsText')}
                    helperText="Optional: Appears before the site name in logo/header (e.g., 'ACME Corporation presents')"
                  />
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    margin="normal"
                    label="Tagline"
                    variant="outlined"
                    placeholder="Empowering students through mathematical excellence"
                    {...tenantForm.register('tagline')}
                    helperText="Short tagline shown on the homepage and in meta descriptions"
                  />
                </Grid>
              </Grid>
              
              <Typography variant="h6" sx={{ mt: 3, mb: 1 }}>
                Contact Information
              </Typography>
              
              <Grid container spacing={2}>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    margin="normal"
                    label="Contact Email"
                    variant="outlined"
                    placeholder="support@example.com"
                    type="email"
                    {...tenantForm.register('contactEmail')}
                    helperText="Support email shown on contact pages"
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    margin="normal"
                    label="Contact Phone"
                    variant="outlined"
                    placeholder="+1 (555) 123-4567"
                    {...tenantForm.register('contactPhone')}
                    helperText="Support phone number"
                  />
                </Grid>
              </Grid>
              
              <Typography variant="h6" sx={{ mt: 3, mb: 1 }}>
                Localization
              </Typography>
              
              <Grid container spacing={2}>
                <Grid item xs={12} sm={6}>
                  <TextField
                    select
                    fullWidth
                    margin="normal"
                    label="Default Language"
                    variant="outlined"
                    defaultValue="en"
                    {...tenantForm.register('language')}
                    SelectProps={{
                      native: true,
                    }}
                    helperText="Primary language for this tenant"
                  >
                    <option value="en">English</option>
                    <option value="es">Spanish</option>
                    <option value="fr">French</option>
                    <option value="de">German</option>
                    <option value="zh">Chinese</option>
                  </TextField>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    select
                    fullWidth
                    margin="normal"
                    label="Timezone"
                    variant="outlined"
                    defaultValue="America/New_York"
                    {...tenantForm.register('timezone')}
                    SelectProps={{
                      native: true,
                    }}
                    helperText="Default timezone for dates and times"
                  >
                    <option value="America/New_York">Eastern Time (ET)</option>
                    <option value="America/Chicago">Central Time (CT)</option>
                    <option value="America/Denver">Mountain Time (MT)</option>
                    <option value="America/Los_Angeles">Pacific Time (PT)</option>
                    <option value="Europe/London">London (GMT)</option>
                    <option value="Asia/Tokyo">Tokyo (JST)</option>
                  </TextField>
                </Grid>
              </Grid>
              
              <Grid container spacing={2}>
                <Grid item xs={12} md={6}>
                  <Typography variant="h6" sx={{ mt: 3, mb: 1 }}>
                    External Links
                  </Typography>
                  
                  <Grid container spacing={2}>
                    <Grid item xs={12}>
                      <TextField
                        fullWidth
                        margin="normal"
                        label="Privacy Policy URL"
                        variant="outlined"
                        placeholder="https://example.com/privacy"
                        {...tenantForm.register('privacyPolicyUrl')}
                        helperText="Custom privacy policy page URL"
                      />
                    </Grid>
                    <Grid item xs={12}>
                      <TextField
                        fullWidth
                        margin="normal"
                        label="Terms of Service URL"
                        variant="outlined"
                        placeholder="https://example.com/terms"
                        {...tenantForm.register('termsUrl')}
                        helperText="Custom terms of service page URL"
                      />
                    </Grid>
                  </Grid>
                </Grid>
                
                <Grid item xs={12} md={6}>
                  <Typography variant="h6" sx={{ mt: 3, mb: 1 }}>
                    Analytics
                  </Typography>
                  
                  <TextField
                    fullWidth
                    margin="normal"
                    label="Google Analytics ID"
                    variant="outlined"
                    placeholder="G-XXXXXXXXXX or UA-XXXXXXXX-X"
                    {...tenantForm.register('gaTrackingId')}
                    helperText="Google Analytics tracking ID for this tenant"
                  />
                </Grid>
              </Grid>
              
              <Grid container spacing={2}>
                <Grid item xs={12} md={8}>
                  <Box sx={{ mt: 2, mb: 3 }}>
                    <Paper sx={{ p: 2, bgcolor: 'grey.100', borderRadius: 2 }}>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                        <Typography variant="subtitle2" color="textSecondary">
                          Header Preview:
                        </Typography>
                        <Typography variant="caption" color="textSecondary">
                          (Colors can be customized in Branding tab)
                        </Typography>
                      </Box>
                      <Box sx={{ 
                        display: 'flex', 
                        alignItems: 'center', 
                        p: 1,
                        borderRadius: 1,
                        bgcolor: tenantBrandingColors.primaryColor 
                      }}>
                        <Box sx={{ 
                          display: 'flex', 
                          flexDirection: 'column', 
                          alignItems: 'flex-start',
                          pl: 1 
                        }}>
                          {tenantForm.watch('presentsText') && (
                            <Typography variant="caption" sx={{
                              fontStyle: 'italic',
                              color: tenantBrandingColors.secondaryColor
                            }}>
                              {tenantForm.watch('presentsText')}
                            </Typography>
                          )}
                          <Typography
                            variant="h6"
                            component="span"
                            sx={{
                              fontWeight: 'bold',
                              color: '#FFFFFF',
                            }}
                          >
                            {tenantForm.watch('siteName') || 'MathWiz League'}
                          </Typography>
                        </Box>
                      </Box>
                    </Paper>
                  </Box>
                </Grid>
                <Grid item xs={12} md={4} sx={{ display: 'flex', alignItems: 'center' }}>
                  <FormControlLabel
                    control={
                      <Switch
                        {...tenantForm.register('active')}
                        defaultChecked={selectedTenant ? selectedTenant.active : true}
                      />
                    }
                    label="Active"
                  />
                </Grid>
              </Grid>
            </Box>
          </DialogContent>
          <DialogActions>
            <Button onClick={handleCloseTenantDialog}>Cancel</Button>
            <Button type="submit" variant="contained" color="primary">
              Save
            </Button>
          </DialogActions>
        </form>
      </Dialog>
      
      {/* Branding Dialog */}
      <Dialog open={openBrandingDialog} onClose={handleCloseBrandingDialog} maxWidth="lg" fullWidth>
        <form onSubmit={brandingForm.handleSubmit(handleBrandingSubmit)}>
          <DialogTitle>
            {selectedTenant ? `Configure Branding: ${selectedTenant.name}` : 'Configure Branding'}
          </DialogTitle>
          <DialogContent>
            <Box sx={{ mt: 2 }}>
              <Typography variant="h6" gutterBottom>
                Colors
              </Typography>
              
              <Grid container spacing={2}>
                <Grid item xs={12} sm={6} md={3}>
                  <TextField
                    fullWidth
                    margin="normal"
                    label="Primary Color"
                    variant="outlined"
                    type="color"
                    {...brandingForm.register('primaryColor')}
                    InputLabelProps={{ shrink: true }}
                  />
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                  <TextField
                    fullWidth
                    margin="normal"
                    label="Secondary Color"
                    variant="outlined"
                    type="color"
                    {...brandingForm.register('secondaryColor')}
                    InputLabelProps={{ shrink: true }}
                  />
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                  <TextField
                    fullWidth
                    margin="normal"
                    label="Tertiary Color"
                    variant="outlined"
                    type="color"
                    {...brandingForm.register('tertiaryColor')}
                    InputLabelProps={{ shrink: true }}
                  />
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                  <TextField
                    fullWidth
                    margin="normal"
                    label="Accent Color"
                    variant="outlined"
                    type="color"
                    {...brandingForm.register('accentColor')}
                    InputLabelProps={{ shrink: true }}
                  />
                </Grid>
              </Grid>
              
              <Typography variant="h6" gutterBottom sx={{ mt: 3 }}>
                Typography
              </Typography>
              
              <TextField
                fullWidth
                margin="normal"
                label="Font Family"
                variant="outlined"
                placeholder="'Poppins', 'Roboto', 'Helvetica', 'Arial', sans-serif"
                {...brandingForm.register('fontFamily')}
              />
              
              <Typography variant="h6" gutterBottom sx={{ mt: 3 }}>
                UI Style
              </Typography>
              
              <Grid container spacing={2}>
                <Grid item xs={12} sm={4}>
                  <TextField
                    select
                    fullWidth
                    margin="normal"
                    label="Button Style"
                    variant="outlined"
                    defaultValue="rounded"
                    {...brandingForm.register('buttonStyle')}
                    SelectProps={{
                      native: true,
                    }}
                  >
                    <option value="rounded">Rounded</option>
                    <option value="square">Square</option>
                    <option value="pill">Pill</option>
                  </TextField>
                </Grid>
                <Grid item xs={12} sm={4}>
                  <TextField
                    select
                    fullWidth
                    margin="normal"
                    label="Card Style"
                    variant="outlined"
                    defaultValue="raised"
                    {...brandingForm.register('cardStyle')}
                    SelectProps={{
                      native: true,
                    }}
                  >
                    <option value="flat">Flat</option>
                    <option value="raised">Raised</option>
                    <option value="outlined">Outlined</option>
                  </TextField>
                </Grid>
                <Grid item xs={12} sm={4}>
                  <TextField
                    select
                    fullWidth
                    margin="normal"
                    label="Theme Mode"
                    variant="outlined"
                    defaultValue="light"
                    {...brandingForm.register('themeMode')}
                    SelectProps={{
                      native: true,
                    }}
                  >
                    <option value="light">Light</option>
                    <option value="dark">Dark</option>
                    <option value="auto">Auto (System)</option>
                  </TextField>
                </Grid>
              </Grid>
              
              <Typography variant="h6" gutterBottom sx={{ mt: 3 }}>
                Assets
              </Typography>
              
              <Grid container spacing={2}>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    margin="normal"
                    label="Logo URL"
                    variant="outlined"
                    placeholder="https://example.com/logo.png"
                    {...brandingForm.register('logoUrl')}
                    InputProps={{
                      startAdornment: <ImageIcon color="action" sx={{ mr: 1 }} />
                    }}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    margin="normal"
                    label="Favicon URL"
                    variant="outlined"
                    placeholder="https://example.com/favicon.ico"
                    {...brandingForm.register('faviconUrl')}
                    InputProps={{
                      startAdornment: <ImageIcon color="action" sx={{ mr: 1 }} />
                    }}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    margin="normal"
                    label="Hero Image URL"
                    variant="outlined"
                    placeholder="https://example.com/hero.jpg"
                    {...brandingForm.register('heroImageUrl')}
                    InputProps={{
                      startAdornment: <ImageIcon color="action" sx={{ mr: 1 }} />
                    }}
                    helperText="Background image for homepage hero section"
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    margin="normal"
                    label="Login Background URL"
                    variant="outlined"
                    placeholder="https://example.com/login-bg.jpg"
                    {...brandingForm.register('loginBackgroundUrl')}
                    InputProps={{
                      startAdornment: <ImageIcon color="action" sx={{ mr: 1 }} />
                    }}
                    helperText="Background image for login/register pages"
                  />
                </Grid>
              </Grid>
              
              <Typography variant="h6" gutterBottom sx={{ mt: 3 }}>
                Custom CSS
              </Typography>
              
              <TextField
                fullWidth
                margin="normal"
                label="Custom CSS"
                variant="outlined"
                multiline
                rows={4}
                {...brandingForm.register('customCss')}
                placeholder="/* Add custom CSS overrides here */\n.header { background: linear-gradient(to right, var(--primary), var(--secondary)); }"
              />
            </Box>
          </DialogContent>
          <DialogActions>
            <Button onClick={handleCloseBrandingDialog}>Cancel</Button>
            <Button type="submit" variant="contained" color="primary">
              Save Branding
            </Button>
          </DialogActions>
        </form>
      </Dialog>
      
      {/* Delete Confirmation Dialog */}
      <Dialog open={openDeleteDialog} onClose={handleCloseDeleteDialog}>
        <DialogTitle>Delete Tenant</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to delete the tenant "{selectedTenant?.name}"?
            This action cannot be undone and will remove all associated data.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDeleteDialog}>Cancel</Button>
          <Button onClick={handleDeleteTenant} color="error" variant="contained">
            Delete
          </Button>
        </DialogActions>
      </Dialog>
    </AdminLayout>
  );
};

export default TenantManagementPage;